import { useState, useEffect } from 'react';
import Modal from './Modal';
import { paymentsApi, formatCurrencyWhole } from '../utils/api';
import { toast } from './Toast';
import { PaymentMethodIcon } from '../pages/SupplierManagementPage';
import { useIsMobile } from '../utils/useIsMobile';
import { useConfirm } from '../utils/useConfirm';

const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y.slice(-2)}`;
};

// What was actually paid on this row. `payments.amount` is NULL for the normal
// case — "the document's full total" — so the column falls back to doc_total
// rather than rendering a blank cell for every historical and auto-pay row
// (see server/lib/paymentAmount.js).
//
// MAGNITUDE, not signed value: a credit note may be stored with a negative
// total (lib/amountOwed.js takes ABS throughout for exactly this reason), and
// settling one in full would otherwise render as "-₪500.00 paid" — money moving
// backwards. The amount a user types is always positive.
const docMagnitude = (p) => Math.abs(Number(p.doc_total || 0));
const paidAmount = (p) => (p.amount != null ? p.amount : docMagnitude(p));

// Only rows a person deliberately overrode get a tag, and it names WHICH way
// the override went. An untouched payment carries no tag at all — tagging the
// ordinary case would make the exceptional one invisible.
function AmountTag({ payment }) {
  if (payment.amount == null || payment.doc_total == null) return null;
  // Compared against the magnitude too — against a raw -500 every positive
  // partial settlement of a credit note read as "מעל הסכום".
  const over = payment.amount > docMagnitude(payment) + 0.005;
  return (
    <span style={{
      fontSize: '0.7rem', fontWeight: 700, borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap',
      background: over ? '#fef3c7' : '#eef2f7',
      color:      over ? '#b45309' : '#4b5563',
    }}>
      {over ? 'מעל הסכום' : 'חלקי'}
    </span>
  );
}

// Audit trail. The server renders the strings (recorded_by_label /
// recorded_at_label via server/lib/paymentAudit.js) so the modal and the Excel
// export can never describe the same payment differently — an earlier revision
// duplicated the formatting here and the two drifted immediately. The client
// only decides STYLING, keyed off recorded_by_kind.
const MUTED_KINDS = new Set(['unknown', 'system']);

// "נרשם ע״י" — author over recorded-at. `inline` lays it out on one line for the
// mobile card; the table stacks it in its own cell.
function RecordedBy({ payment, inline = false }) {
  const muted = MUTED_KINDS.has(payment.recorded_by_kind);
  const who = (
    <span style={{ color: muted ? '#bbb' : 'var(--ink-muted)', fontStyle: muted ? 'italic' : 'normal' }}>
      {payment.recorded_by_label}
    </span>
  );
  const when = payment.recorded_at_label;

  if (inline) {
    return (
      <>
        <span style={{ color: '#999' }}>נרשם ע״י: </span>
        {who}
        {when && <span style={{ color: '#aaa' }}> · {when}</span>}
      </>
    );
  }
  return (
    <>
      <div>{who}</div>
      {when && <div style={{ fontSize: '0.75rem', color: '#aaa' }}>{when}</div>}
    </>
  );
}

export default function PaymentHistoryModal({ supplierId, supplierName, canDelete, onClose, onChange }) {
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  // The table is 6 columns wide once "נרשם ע״י" is added — past a phone's width,
  // so the last column got clipped off the edge. Below 720px each payment
  // renders as a stacked card instead, matching how the supplier lists behave.
  const isMobile = useIsMobile();
  const [askConfirm, confirmDialog] = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const r = await paymentsApi.getBySupplier(supplierId);
      setPayments(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same swap as the supplier-documents page: window.confirm returns false
  // wherever a browser suppresses dialogs, and the guard then returned without
  // deleting anything and without telling anyone.
  //
  // It also has to name WHICH payment now. A document may carry several since
  // partial payments landed, so a bare "delete this payment record?" over a
  // list of three identical-looking rows is a guess.
  const handleDelete = (p) => {
    const amount = p.amount != null ? p.amount : Math.abs(Number(p.doc_total || 0));
    askConfirm({
      title: 'מחיקת רשומת תשלום',
      message: `למחוק את התשלום על סך ${formatCurrencyWhole(amount)} מ-${fmtDate(p.payment_date)}?`,
      detail: p.doc_num
        ? `${p.doc_type} · ${p.doc_num}${p.notes ? ` — "${p.notes}"` : ''}`
        : 'תשלום כללי',
      confirmLabel: 'מחק תשלום',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await paymentsApi.delete(p.id);
          toast('רשומת התשלום נמחקה');
          load();
          // Notify the parent so the supplier listing re-fetches and the badge
          // recomputes. Without this, paid_docs is stale and the row keeps
          // showing שולם even after every payment has been deleted.
          onChange?.();
        } catch {
          toast('שגיאה במחיקה', 'error');
        }
      },
    });
  };

  return (
    <Modal onClose={onClose} title={`היסטוריית תשלומים — ${supplierName}`} size={780}>

    {loading ? (
      <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
    ) : payments.length === 0 ? (
      <div className="empty-state" style={{ padding: '32px 0' }}>
            
        <p>אין תשלומים רשומים עבור ספק זה</p>
      </div>
    ) : isMobile ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {payments.map(p => {
          return (
            <div key={p.id} style={{
              border: '1px solid #e6e6e6', borderRadius: 10, padding: '10px 12px',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{fmtDate(p.payment_date)}</span>
                  <span style={{ fontWeight: 700 }}>{formatCurrencyWhole(paidAmount(p))}</span>
                  <AmountTag payment={p} />
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.9rem' }}>
                  <PaymentMethodIcon method={p.payment_method} size={14} /> {p.payment_method}
                </span>
              </div>

              <div style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
                {p.doc_num
                  ? `${p.doc_type} · ${p.doc_num}`
                  : <span style={{ color: '#aaa' }}>תשלום כללי</span>}
              </div>

              {p.notes && (
                <div style={{ fontSize: '0.85rem', color: '#666' }}>{p.notes}</div>
              )}

              <div style={{ fontSize: '0.8rem', borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
                <RecordedBy payment={p} inline />
              </div>

              {canDelete && (
                <button className="btn btn-sm btn-danger" style={{ alignSelf: 'flex-start' }}
                        onClick={() => handleDelete(p)}>מחק</button>
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>תאריך</th>
              <th>סכום</th>
              <th>אמצעי תשלום</th>
              <th>מסמך</th>
              <th>הערות</th>
              <th>נרשם ע״י</th>
              {canDelete && <th style={{ width: 60 }}></th>}
            </tr>
          </thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 600 }}>{fmtDate(p.payment_date)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ fontWeight: 700 }}>{formatCurrencyWhole(paidAmount(p))}</span>
                  {p.amount != null && <div style={{ marginTop: 2 }}><AmountTag payment={p} /></div>}
                </td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <PaymentMethodIcon method={p.payment_method} size={14} /> {p.payment_method}
                  </span>
                </td>
                <td style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
                  {p.doc_num
                    ? `${p.doc_type} · ${p.doc_num}`
                    : <span style={{ color: '#aaa' }}>תשלום כללי</span>}
                </td>
                <td style={{ fontSize: '0.85rem', color: '#666' }}>{p.notes || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  <RecordedBy payment={p} />
                </td>
                {canDelete && (
                  <td>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p)}>מחק</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Rendered inside this modal's shell, but ConfirmModal now portals to
        <body> like every other dialog — so it lands after this modal in the
        DOM and paints above it at the same z-index, rather than relying on
        position:fixed to escape the history list. */}
    {confirmDialog}

    <div className="modal-actions" style={{ marginTop: 20 }}>
      <button className="btn btn-secondary" onClick={onClose}>סגור</button>
    </div>
    </Modal>
  );
}
